import {
  ALLOWED_MEDIA_TYPE_MIMES_IN_FILTER,
  BrokerPortfolioStatusesEnum,
  BrokerPortfolioTypesEnum,
  FileModel,
} from 'dash-common';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  DashPlaceholder,
  Flex,
  Text,
  SortHeader,
  Image,
  useScreenResolution,
  FileTypeIcon,
  Button,
} from 'dash-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PortfolioListItemFragment, useGetPortfolioFolderItemsQuery } from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';
import PortfolioActionsDropDown from 'pages/portfolios/PortfoliosPage/components/PortfoliosPageView/components/PortfolioActionsDropDown/PortfolioActionsDropDown';

import { StyledTable, VideoContainer } from './PortfoliosListTableView.styles';
import ActionCell from './components/ActionCell';

const PortfoliosListTableView = ({
  portfolios,
  loading,
}: {
  portfolios: PortfolioListItemFragment[];
  loading: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();

  const columns = useMemo(() => {
    const isResponsive = isTablet || isMobile;

    return [
      {
        Header: () => (
          <SortHeader variation="green" accessor="name">
            {t('pages:portfoliosPage.portfolioName')}
          </SortHeader>
        ),
        ...(isMobile ? { sticky: 'left', width: 163 } : {}),
        accessor: 'name',
        Cell: ({
          value,
          row: { original },
        }: {
          value: string;
          row: { original: PortfolioListItemFragment };
        }) => {
          const { type, _id: portfolioId, brokerId } = original;
          const isFolder = type === BrokerPortfolioTypesEnum.FOLDER;

          const { data } = useGetPortfolioFolderItemsQuery({
            variables: {
              portfolioFolderId: original._id,
            },
            skip: !isFolder,
          });
          const propertyData = data?.getPortfolioFolderItems?.collection[0]
          const file = propertyData?.property?.mainPropertyMediaFile?.files[0];
          const isVideo = ALLOWED_MEDIA_TYPE_MIMES_IN_FILTER.VIDEOS.includes(
            file?.type || ''
          );

          const thumbnail = isVideo ? (
            <VideoContainer
              w={48}
              h={48}
              justifyContent="center"
              alignItems="center"
              mr={2}
            >
              <FileTypeIcon file={file as FileModel} />
            </VideoContainer>
          ) : (
            <Image src={file?.thumbnailFile?.publicUrl || ''} mr={2} />
          );

          return (
            <Flex
              alignItems="center"
              onClick={() => {
                const isFolder = type === BrokerPortfolioTypesEnum.FOLDER;
                if (isFolder) {
                  return navigate(
                    RoutesPath.PortfolioDetails.replace(':id', portfolioId)
                  );
                }
                window?.open(
                  RoutesPath.PortfolioPreview.replace(
                    ':brokerId',
                    brokerId
                  ).replace(':portfolioId', portfolioId),
                  '_blank'
                );
              }}
            >
              {file ? (
                thumbnail
              ) : (
                <DashPlaceholder variation="secondary" mr={2} />
              )}
              <Text>{value}</Text>
            </Flex>
          );
        },
      },
      {
        Header: () => (
          <SortHeader variation="green" accessor="status">
            {t('common:labels.status')}
          </SortHeader>
        ),
        accessor: 'status',
        Cell: ({ value }: { value: BrokerPortfolioStatusesEnum }) => (
          <Text>{t(`pages:portfoliosPage.statuses.${value}`)}</Text>
        ),
      },
      {
        Header: () => (
          <SortHeader variation="green" accessor="viewsCount">
            {t('pages:portfoliosPage.views')}
          </SortHeader>
        ),
        accessor: 'viewsCount',
      },
      {
        accessor: '_id',
        Cell: ({
          _id,
          row,
        }: {
          _id: string;
          row: { original: PortfolioListItemFragment };
        }) => {
          return isResponsive ? (
            <PortfolioActionsDropDown
              isFixed={false}
              portfolio={row.original}
              Anchor={() => <Button square icon="dots" h={32} />}
            />
          ) : (
            <ActionCell _id={_id} portfolio={row.original} />
          );
        },
      },
    ];
  }, [isTablet, isMobile, theme, navigate, t]);

  return (
    <StyledTable
      tableProps={{
        data: portfolios,
        columns,
        // min height in case if there no data and loading in progress, we need to avoid loading collapse over headers
        minHeight: 150,
        loading,
      }}
      paginationProps={{
        previousButtonLabel: t('common:labels.previous'),
        nextButtonLabel: t('common:labels.next'),
        label: isMobile ? '' : t('pages:portfoliosPage.paginationLabel'),
      }}
    />
  );
};

export default PortfoliosListTableView;
